import {
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue
} from '@databases/types/database';
import {
  isValidHyperlinkUrl,
  makeValidHyperlink
} from '@databases/utils/database/field-types/hyperlink-normalization';
import { createElement, type ReactNode } from 'react';

import { defineFieldReadModule, type FieldReadModule } from '../field-read-module.ts';
import {
  EMPTY_HYPERLINK_CONTENT,
  type HyperlinkReadContent,
  type HyperlinkItemContent
} from './hyperlink-read-content.ts';
import { HyperlinkValueView } from './hyperlink-value-view.tsx';

/**
 * Mirrors V1's fallback without importing its stateful utility barrel or changing
 * existing consumers. Keep the differential V1 tests as the parity boundary.
 */
function getFallbackHyperlinkLabel(url: string): string {
  if (!isValidHyperlinkUrl(url)) {
    return url;
  }

  return new URL(url).pathname.match(/\/browse\/([A-Z]+-[0-9]+)$/)?.[1] ?? url;
}

function compileLink(
  hyperlink: DatabaseValue<DatabaseFieldType.HYPERLINK>['value'][number],
  resolve: boolean
): HyperlinkItemContent {
  const normalized = makeValidHyperlink(hyperlink);
  const href = isValidHyperlinkUrl(normalized.url) ? normalized.url : undefined;
  const label =
    normalized.isLabelExplicit && normalized.label
      ? normalized.label
      : resolve
        ? normalized.label || getFallbackHyperlinkLabel(normalized.url)
        : normalized.url;

  return Object.freeze({
    key: hyperlink._id,
    url: normalized.url,
    href,
    label,
    resolve: resolve && Boolean(href)
  });
}

function createHyperlinkContent(
  value: DatabaseValue<DatabaseFieldType.HYPERLINK>['value'],
  resolve: boolean
): HyperlinkReadContent {
  if (value.length === 0) {
    return EMPTY_HYPERLINK_CONTENT;
  }

  return Object.freeze<HyperlinkReadContent>({
    kind: 'hyperlink',
    links: Object.freeze(value.map((link) => compileLink(link, resolve)))
  });
}

function convertTextValue(plain: string): DatabaseValue<DatabaseFieldType.HYPERLINK> {
  return {
    type: DatabaseFieldType.HYPERLINK,
    value: plain
      .split(/\s+/)
      .filter(isValidHyperlinkUrl)
      .map((url, index) => ({ _id: `legacy:${index}:${url}`, url }))
  };
}

type HyperlinkReadRenderer = (content: HyperlinkReadContent) => ReactNode;

const renderHyperlinkValue: HyperlinkReadRenderer = (content) =>
  createElement(HyperlinkValueView, { content });

/**
 * Captures resolution settings once; the application's existing provider owns links.
 */
export function createHyperlinkFieldReadModule({
  render = renderHyperlinkValue
}: {
  readonly render?: HyperlinkReadRenderer;
} = {}): FieldReadModule<HyperlinkReadContent> {
  return defineFieldReadModule<HyperlinkReadContent>({
    fieldType: DatabaseFieldType.HYPERLINK,
    render,
    compile(field: DatabaseField) {
      const resolve = field.settings.resolve;

      return Object.freeze({
        fieldId: field.id,
        resolve(
          storedValue: DatabaseValue | undefined,
          defaultValue: DatabaseValue | undefined
        ): HyperlinkReadContent {
          const value = storedValue ?? defaultValue;

          if (value?.type === DatabaseFieldType.HYPERLINK) {
            return createHyperlinkContent(value.value, resolve);
          }

          if (value?.type === DatabaseFieldType.TEXT) {
            return createHyperlinkContent(
              convertTextValue(value.value.plain).value,
              resolve
            );
          }

          return EMPTY_HYPERLINK_CONTENT;
        }
      });
    }
  });
}
